import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';

import ColorLineText from '../../atoms/Text/ColorLineText';

import { ItemComboBox, ComboBoxItemType } from '../../atoms/ComboBox/ItemComboBox';
import { MeasureInfoType, GetMeasureInfoType, GetDataTimeList, SelectDataType } from './RawWaveformComparison';
import ItemCalender from '../../atoms/Calendar/ItemCalender';
import ItemCheckBox from '../../atoms/CheckBox/ItemCheckBox';
import DateInput from '../../atoms/Calendar/DateInput';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(
  createStyles({
    tableCell: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderStyle: 'solid',
      padding: '0px 0px 0px 4px',
      borderBottomWidth: '0px',
    },
    labelStyle: {
      fontSize: '13px',
    },
    itemBoxStyle: {
      fontSize: '13px',
    },
  })
);

type Props = {
  selectData: SelectDataType;
  itemColor: string;
  mesInfoList: MeasureInfoType[];
  itemName: string;
  onChangeSelectDataEvent: (selectData: SelectDataType) => void;
  isBlankSelect: boolean;
  onChnageIsShowSelect: (selectData: SelectDataType) => void;
  onAutoRangeSet: (data: boolean) => void;
};

/**
 * 機器種別リスト作成
 * @param data 
 * @param measureId 
 * @returns 
 */
const CreateDevList = (data: MeasureInfoType[], measureId: number): ComboBoxItemType[] => {
  var ret: ComboBoxItemType[] = [];
  if (data != null) {
    var mes = data.find((item) => item.id === measureId);
    //speed2,4でfilter後、groupby
    var devList = data.filter(
      (el: MeasureInfoType, index, self) =>
        self.findIndex((e: MeasureInfoType) => (e.Speed === 2 || e.Speed === 4) && e.DevIndex === el.DevIndex) === index
    );
    console.debug(['CreateDevList', devList]);
    console.debug(devList);
    for (var i in devList) {
      ret.push({
        index: devList[i].id,
        name: devList[i].DevNo + devList[i].DevName,
      });
    }
  }
  return ret;
};

/**
 * 項目種別リスト作成
 * @param data 
 * @param measureId 
 * @param devId 
 * @returns 
 */
const CreateItemList = (data: MeasureInfoType[], measureId: number, devId: number): ComboBoxItemType[] => {
  var ret: ComboBoxItemType[] = [];
  if (data != null) {
    var mes = data.find((item) => item.id === measureId);
    var dev = data.find((item) => item.id === devId);
    //speed2,4でfilter後、groupby
    var itemNameList = data.filter(
      (el: MeasureInfoType, index, self) =>
        self.findIndex(
          (e: MeasureInfoType) =>
            (e.Speed === 2 || e.Speed === 4) &&
            e.KindNo === mes?.KindNo &&
            e.Speed === mes?.Speed &&
            e.DevIndex === dev?.DevIndex &&
            e.ItemNameNo == el.ItemNameNo &&
            e.ItemName == el.ItemName
        ) === index
    );

    for (var i in itemNameList) {
      ret.push({ index: itemNameList[i].id, name: itemNameList[i].ItemName });
    }
  }
  return ret;
};

/**
 * 時刻リスト生成
 * @param data 
 * @returns 
 */
export const CreateTimeList = (data: string[]): ComboBoxItemType[] => {
  var ret: ComboBoxItemType[] = [];

  for (var i in data) {
    var time = dayjs(data[i]).format('HH:mm:ss');
    ret.push({ index: Number(i + 1), name: time });
  }
  return ret;
};

const RawWaveformComparisonSelectItem: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [selectDay, setSelectDay] = React.useState<Date>(new Date());
  const [mesList, setMesList] = React.useState<ComboBoxItemType[]>([]);
  const [devList, setDevList] = React.useState<ComboBoxItemType[]>([]);
  const [itemList, setItemList] = React.useState<ComboBoxItemType[]>([]);
  const [timeList, setTimeList] = useState<ComboBoxItemType[]>();
  const [selectData, setSelectData] = useState<SelectDataType>(props.selectData);
  const [selectTimeid, setSelectTimeid] = useState<number>(0);

  /**
    * ローパスフィルタ切替
    */
  const lowPassList: ComboBoxItemType[] = [
    { index: 0, name: t('RawWaveformComparisonSelectItem.なし', 'なし') },
    { index: 1, name: t('RawWaveformComparisonSelectItem.あり', 'あり') },
  ];

  /**
   * 計測種別リスト作成
   * @param data 
   * @param isBlankSelect 
   * @returns 
   */
const CreateMeasureList = (data: MeasureInfoType[], isBlankSelect: boolean): ComboBoxItemType[] => {
  var ret: ComboBoxItemType[] = [];
  if (data != null) {
    //speed2でfilter後、groupby
    var measureList : MeasureInfoType[] =[];

    if(props.selectData.isLowPassFlg){
      //ローパスフィルタ有効時 零相・逆相無効
      var filterList = data.filter(      
      (el: MeasureInfoType, index, self) =>
        self.findIndex(
          (e: MeasureInfoType) => (e.Speed === 2) && e.KindNo === el.KindNo && e.MesName === el.MesName
        ) === index
      );
      //cloud版での零相・逆相無効
      measureList = filterList.filter(
        (el: MeasureInfoType, index, self) =>
        el.KindNo !== 7 && el.KindNo !== 9
      )
    }
    else{
      //ローパスフィルタ無効時 零相・逆相有効
      measureList = data.filter(      
      (el: MeasureInfoType, index, self) =>
        self.findIndex(
          (e: MeasureInfoType) => (e.Speed === 2 || e.Speed === 4) && e.KindNo === el.KindNo && e.MesName === el.MesName
        ) === index
      );
    }
    
    console.debug(['RawWaveformComparisonSelectItem - MeasureList,CreateMeasureList', data, measureList]);
    for (var i in measureList) {
      ret.push({ index: measureList[i].id, name: measureList[i].MesName });
    }

    if (isBlankSelect) ret.push({ index: -1, name: '' });
  }
  return ret;
};


  //コンボボックスデータ作成
  useEffect(() => {
    if (props.mesInfoList.length != 0) {
      console.debug(['★★★初期表示でのCrete',props.mesInfoList]);
      console.debug(props.selectData);
      var sdt = props.selectData;
      var mesList = CreateMeasureList(props.mesInfoList, props.isBlankSelect);
      var devList = CreateDevList(props.mesInfoList, sdt.mesid);
      var itemList = CreateItemList(props.mesInfoList, sdt.mesid, sdt.devid);

      setMesList(mesList);
      setDevList(devList);
      setItemList(itemList);
      setSelectDay(sdt.selectDate);

      createTimeList(sdt.mesid, sdt.devid, sdt.itemid, sdt.selectDate, sdt.selectTime);
    }
  }, [props.mesInfoList,props.selectData.isLowPassNo]);

  /**
   * 時刻リスト作成
   * @param mesid 
   * @param devid 
   * @param itemid 
   * @param date 
   * @param time 
   * @returns 
   */
  const createTimeList = (mesid: number, devid: number, itemid: number, date: Date, time: string) => {
    console.debug(['createTimeList', mesid]);

    if (mesid === -1) {
      var sdt: SelectDataType = {
        ...selectData,
        mesid: -1,
        devid: -1,
        itemid: -1,
        selectDate: date,
        selectTime: '',
        isLowPassFlg:false,
      };
      setTimeList([]);
      setSelectData(sdt);
      props.onChangeSelectDataEvent(sdt);
      console.debug(['std', sdt]);
      return;
    }

    if (props.mesInfoList.length != 0) {
      var mes = props.mesInfoList.find((item) => item.id == mesid);
      var dev = props.mesInfoList.find((item) => item.id == devid);
      var itemdt = props.mesInfoList.find((item) => item.id == itemid);
      var day = dayjs(date).format('YYYY-MM-DD');

      if (mes && dev && itemdt) {
        //対象レコード取得
        var target = props.mesInfoList.find((item) => {
          return (
            item.KindNo === mes?.KindNo && item.DevIndex === dev?.DevIndex && item.ItemNameNo === itemdt?.ItemNameNo
          );
        });
        if (target) {
          //時間リスト取得
          GetDataTimeList(Number(target?.PlcNo), Number(target?.AiCard), Number(target?.AiCh), day,selectData.isLowPassFlg).then((ret) => {
            if (ret) {
              var sTime: string = '';
              var timeList = CreateTimeList(ret);

              //時間でソートを行う
              timeList.sort(function (a, b) {
                if (a.name > b.name) {
                  return 1;
                } else {
                  return -1;
                }
              });
              setTimeList(timeList);
              if (timeList.length != 0) {
                var t = timeList.find((row) => row.name === time);
                if (t) {
                  setSelectTimeid(t.index);
                } else {
                  setSelectTimeid(timeList[0].index);
                  time = timeList[0].name;
                }
              }
              var sdt: SelectDataType = {
                ...selectData,
                mesid: mesid,
                devid: devid,
                itemid: itemid,
                selectDate: date,
                selectTime: time,
              };
              setSelectData(sdt);
              props.onChangeSelectDataEvent(sdt);
              console.debug(['std', sdt]);
            }
          });
        } else {
          var sdt: SelectDataType = {
            ...selectData,
            mesid: -1,
            devid: -1,
            itemid: -1,
            selectDate: date,
            selectTime: '',
          };
          setSelectData(sdt);
          props.onChangeSelectDataEvent(sdt);
          console.debug(['std', sdt]);
        }
      }
    }
  };

  const handleShowChange = () => {
    var target = selectData;
    target.isShow = !target.isShow;
    setSelectData({ ...target });
    props.onChnageIsShowSelect(target);
  };

  /**
   * 計測種別変更event
   * @param event 
   */
  const ChangeMesSelect = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    var selectId = Number(event.target.value);
    props.onAutoRangeSet(true);
    if (selectId == -1) {
      var devList = CreateDevList(props.mesInfoList, -1);
      var itemList = CreateItemList(props.mesInfoList, -1, -1);
      setDevList(devList);
      setItemList(itemList);
      createTimeList(-1, -1, -1, selectDay, '');
    } else {
      var mes = props.mesInfoList.find((item) => item.id === Number(event.target.value));
      if (mes != undefined) {
        var devList = CreateDevList(props.mesInfoList, mes.id);
        var itemList = CreateItemList(props.mesInfoList, mes.id, devList[0].index);
        setDevList(devList);
        setItemList(itemList);
        createTimeList(mes.id, devList[0].index, itemList[0].index, selectDay, '');
      }
    }
  };
  /**
   * 機器変更event
   * @param event 
   */
  const ChangeDevSelect = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    var mes = props.mesInfoList.find((item) => item.id === selectData.mesid);
    var dev = props.mesInfoList.find((item) => item.id === Number(event.target.value));
    if (mes != undefined && dev != undefined) {
      var itemList = CreateItemList(props.mesInfoList, mes.id, dev.id);
      setItemList(itemList);
      if (itemList.length != 0) {
        createTimeList(mes.id, dev.id, itemList[0].index, selectDay, '');
      }
    }
  };

  /**
   * 項目変更event
   * @param event 
   */
  const ChangeitemSelect = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    var mes = props.mesInfoList.find((item) => item.id === selectData.mesid);
    var dev = props.mesInfoList.find((item) => item.id === selectData.devid);
    var itm = props.mesInfoList.find((item) => item.id === Number(event.target.value));
    if (mes != undefined && dev != undefined && itm != undefined) {
      createTimeList(mes.id, dev.id, itm.id, selectDay, '');
    }
  };

  /**
   * 計測日変更event
   * @param value 
   */
  const onChangeCalendarValue = (value: string) => {
    var s = new Date(value);
    setSelectDay(s);
    createTimeList(selectData.mesid, selectData.devid, selectData.itemid, s, '');
  };

  /**
   * 時刻変更event
   * @param event 
   */
  const ChangeTimeSelect = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    var time = timeList?.find((row) => row.index == Number(event.target.value));
    if (time) {
      var sdt: SelectDataType = {
        ...selectData,
        selectTime: time?.name,
      };
      setSelectData(sdt);
      props.onChangeSelectDataEvent(sdt);
      setSelectTimeid(time.index);
    }
  };
  /**
   * 選択項目チェック変更event
   * @param event 
   */
  const handleIsSelectChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    console.debug(['handleIsSelectChange - selectData=', selectData]);
    setSelectData({ ...selectData, isSelect: !selectData.isSelect });
    props.onChangeSelectDataEvent({
      ...selectData,
      isSelect: !selectData.isSelect,
    });
  };

  /**
   * ローパスフィルタ有効無効変更event
   * @param event 
   */
  const LowPassSelectChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    console.debug(['LowPassSelectChange - selectData=', selectData]);
     const lowPassNo = Number(event.target.value);
    var sdt :SelectDataType = {
      ...selectData, 
      isLowPassNo: lowPassNo,
      isLowPassFlg: lowPassNo === 1,
    }
    setSelectData({ ...sdt });
    props.onChangeSelectDataEvent(sdt);
  };

  return (
    <Table>
      <TableBody>
        <TableRow>
          <TableCell className={classes.tableCell} colSpan={2} onChange={handleShowChange}>
            <input type="checkbox" checked={selectData.isShow} />
            {props.itemName}
            <ColorLineText color={props.itemColor} width={'60px'} height={'2px'} />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={2} className={classes.tableCell}>
            <ItemComboBox
              label={t('RawWaveformComparisonSelectItem.計測種別', '計測種別：')}
              itemList={mesList}
              value={selectData.mesid}
              labelStyle={classes.labelStyle}
              itemBoxStyle={classes.itemBoxStyle}
              onChangeEvent={ChangeMesSelect}
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={2} className={classes.tableCell}>
            <ItemComboBox
              label={t('RawWaveformComparisonSelectItem.機器種別', '機器種別：')}
              itemList={devList}
              value={selectData.devid}
              labelStyle={classes.labelStyle}
              itemBoxStyle={classes.itemBoxStyle}
              onChangeEvent={ChangeDevSelect}
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={2} className={classes.tableCell}>
            <ItemComboBox
              label={t('RawWaveformComparisonSelectItem.項目', '項目：')}
              itemList={itemList}
              value={selectData.itemid}
              labelStyle={classes.labelStyle}
              itemBoxStyle={classes.itemBoxStyle}
              onChangeEvent={ChangeitemSelect}
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={classes.tableCell}>
            <DateInput
              labelCssString={classes.labelStyle}
              initialValue={dayjs(selectDay).format('YYYY-MM-DD')}
              label={t('RawWaveformComparisonSelectItem.計測日', '計測日：')}
              labelPosition={1}
              onDateChange={onChangeCalendarValue}
            ></DateInput>
          </TableCell>
          <TableCell className={classes.tableCell} width={'30%'}>
            <ItemComboBox
              label={''}
              labelStyle={classes.labelStyle}
              itemBoxStyle={classes.itemBoxStyle}
              itemList={timeList}
              value={selectTimeid}
              onChangeEvent={ChangeTimeSelect}
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={classes.tableCell} width={'30%'}>
            <ItemCheckBox
              label={t('RawWaveformComparisonSelectItem.選択項目', '選択項目')}
              labelPosition={1}
              labelStyle={classes.labelStyle}
              value={selectData.isSelect} 
              onChangeEvent={handleIsSelectChange}
            />
          </TableCell>
          <TableCell  className={classes.tableCell}>
            <ItemComboBox
              label={t('RawWaveformComparisonSelectItem.ローパスフィルタ', 'ローパスフィルタ：')}
              itemList={lowPassList}
              value={selectData.isLowPassNo}
              labelStyle={classes.labelStyle}
              itemBoxStyle={classes.itemBoxStyle}
              onChangeEvent={LowPassSelectChange}
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

export default RawWaveformComparisonSelectItem;
